import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';

import { CanDeactivateGuard } from './guards/can-deactivate.guard';
import { meResolver } from './resolvers/me.resolver';
import { AuthService } from './services/auth.service';
import { UsersRoleOptions } from './types/pocketbase-types';

const skipLoginGuard = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const pb = authService.pb;

  // If the authentication token is valid, redirect to home page
  if (pb.authStore.isValid) {
    await router.navigate(['/orders']);
    return false;
  }

  return true;
};

const loginGuard = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const pb = authService.pb;

  // If the authentication token is invalid, redirect to login
  if (!pb.authStore.isValid) {
    await router.navigate(['/login']);
    return false;
  }

  // Automatically refresh the token at application startup
  try {
    await pb.collection('users').authRefresh();
  } catch (error) {
    // If the refresh fails, the token was invalidated. Clear the auth store and navigate to login
    pb.authStore.clear();
    await router.navigate(['/login']);
    return false;
  }

  return true;
};

export interface ComponentCanDeactivate {
  canDeactivate: () => Observable<boolean> | boolean;
}

const unsavedChangesGuard = (component: ComponentCanDeactivate) => component.canDeactivate();
const adminGuard = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const user = await firstValueFrom(authService.getMe());

  if (user.role === UsersRoleOptions.admin) return true;

  await router.navigate(['/']);
  return false;
};

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/login-page/login-page.component'),
    canActivate: [skipLoginGuard],
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./pages/forgot-password-page/forgot-password-page.component'),
    canActivate: [skipLoginGuard],
  },
  {
    path: 'reset-password',
    loadComponent: () => import('./pages/reset-password-page/reset-password-page.component'),
    canActivate: [skipLoginGuard],
  },
  {
    path: 'confirm-password-change',
    loadComponent: () => import('./pages/confirm-password-change-page/confirm-password-change-page.component'),
    canActivate: [skipLoginGuard],
  },
  {
    path: '',
    loadComponent: () => import('./pages/main-page/main-page.component'),
    canActivate: [loginGuard],
    resolve: { me: meResolver },
    children: [
      {
        path: 'orders',
        loadComponent: () => import('./pages/orders-page/orders-page.component'),
      },
      {
        path: 'orders/:id/review',
        loadComponent: () => import('./pages/review-lock-page/review-lock-page.component'),
        canDeactivate: [CanDeactivateGuard],
      },
      {
        path: 'orders/:id/:fragmentId/serials',
        loadComponent: () => import('./pages/batch-serials-page/batch-serials-page.component'),
        canDeactivate: [CanDeactivateGuard],
      },
      {
        path: 'orders/:id',
        loadComponent: () => import('./pages/order-detail-page/order-detail-page.component'),
      },
      {
        path: 'in-progress',
        loadComponent: () => import('./pages/orders-page/orders-page.component'),
      },
      {
        path: 'settings',
        loadComponent: () => import('./pages/settings-page/settings-page.component'),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'contact',
        loadComponent: () => import('./pages/contact-page/contact-page.component'),
      },
      {
        path: 'users',
        loadComponent: () => import('./pages/users-page/users-page.component'),
        canActivate: [adminGuard],
      },
      {
        path: 'users/:id',
        loadComponent: () => import('./pages/user-details-page/user-details-page.component'),
        canActivate: [adminGuard],
      },
      {
        path: 'data-review',
        loadComponent: () => import('./pages/data-review-page/data-review-page.component'),
        canActivate: [adminGuard],
      },
      {
        path: 'data-review/:id',
        loadComponent: () => import('./pages/data-review-details-page/data-review-details-page.component'),
        canActivate: [adminGuard],
      },
      {
        path: 'requests',
        loadComponent: () => import('./pages/requests-page/requests-page.component'),
        canActivate: [adminGuard],
      },
      { path: '', redirectTo: '/orders', pathMatch: 'full' },
    ],
  },
];
